import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useDiseaseAreas } from "@/api/hooks/diseaseAreas";
import { useEnrollSiteInDiseaseArea } from "@/api/hooks/siteDiseaseAreas";
import type { DiseaseAreaRead } from "@/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  siteId: string;
  enrolledDaIds: Set<string>;
  /** Called with the newly-enrolled DA's code so the parent can navigate
   * the tab bar to that DA. */
  onAdded: (daCode: string) => void;
}

export function AddDiseaseAreaDialog({
  open,
  onClose,
  siteId,
  enrolledDaIds,
  onAdded,
}: Props) {
  const allDAs = useDiseaseAreas();
  const enroll = useEnrollSiteInDiseaseArea(siteId);

  const [selectedDaId, setSelectedDaId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const availableDAs: DiseaseAreaRead[] = useMemo(
    () =>
      (allDAs.data ?? []).filter(
        (da: { id: string; code: string; name: string }) => !enrolledDaIds.has(da.id),
      ) as DiseaseAreaRead[],
    [allDAs.data, enrolledDaIds],
  );

  const reset = () => {
    setSelectedDaId("");
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const onConfirm = async () => {
    if (!selectedDaId) {
      setError("Pick a disease area.");
      return;
    }
    const da = availableDAs.find((d) => d.id === selectedDaId);
    if (!da) {
      setError("Disease area no longer available.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await enroll.mutateAsync({ daId: selectedDaId });
      onAdded(da.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enroll");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add disease area</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {availableDAs.length === 0 ? (
          <Typography color="text.secondary">
            This site is already enrolled in every disease area.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick a disease area to enroll this site in. You'll choose which
              locations participate from inside the new tab.
            </Typography>
            <TextField
              select
              fullWidth
              label="Disease area"
              value={selectedDaId}
              onChange={(e) => setSelectedDaId(e.target.value)}
            >
              {availableDAs.map((da) => (
                <MenuItem key={da.id} value={da.id}>
                  {da.name} ({da.code})
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving || availableDAs.length === 0 || !selectedDaId}
        >
          {saving ? "Adding…" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
